/**
 * EditActivityButton Component
 *
 * Simple button component that opens the edit modal.
 * Used in SummaryPriceDetailsForCartItem to trigger editing.
 */

import { Button } from '@mui/material';
import { useLocale } from '@repo/i18n';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useCustomizations } from 'src/components/utils/theme/customizations';

import type { JSX } from 'react';

/**
 * Props for EditActivityButton component
 */
export type EditActivityButtonProps = {
    /** CSS class object for inverted colors theme */
    invertedColorsClass?: Record<string, unknown>;

    /** Handler for edit button click */
    onEditClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;

    /** Item name for accessible label context */
    itemName?: string;
};

export default function EditActivityButton(props: EditActivityButtonProps): JSX.Element {
    const { t } = useLocale();
    const customizations = useCustomizations();

    // Create accessible label with context if itemName provided
    const ariaLabel = props.itemName
        ? `${capitalize(t.edit)} ${props.itemName}`
        : capitalize(t.edit);

    return (
        <Button
            aria-label={ariaLabel}
            sx={{
                padding: 0,
                minWidth: 'auto',
                color: customizations.linkColor,
                textDecoration: `${customizations.linkStyle} !important`,
                ...props.invertedColorsClass,
            }}
            variant="text"
            onClick={props.onEditClick}
        >
            {capitalize(t.edit)}
        </Button>
    );
}
